/**
 * Stories (Backlog 060) — moderation only.
 *
 * Lists every Story, hidden ones included, with what a moderator needs to judge it: review state, area,
 * visibility, size, rating, reviews, Featured rank and access. The actions are moderation, never authoring (the
 * Website edits Stories): approve, reject with a reason (any user can make a Story; nothing is listed until a
 * moderator approves it), set/clear the Featured rank (editorial, Admin only — never at the request of a
 * business or sponsor), hide/unhide with a reason, read and remove reviews, and open the Story on the play site.
 *
 * Tabs: Review queue (Pending, oldest first, with a count) | Stories | Reviews (one Story's reviews, `?story=`)
 * | Paid (placeholder until paid Stories exist).
 */

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import {
  AutoStories,
  CheckCircleOutline,
  HighlightOff,
  HourglassTop,
  OpenInNew,
  Paid,
  RateReview,
  Star,
  StarBorder,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { PageShell } from '@components/common/PageShell'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import type { RowAction } from '@components/common/RowActions'
import { SearchField } from '@components/common/SearchField'
import { useAuthStore } from '@store/authStore'
import { storiesApi, storyPlaySiteUrl } from '@api/endpoints/stories'
import {
  STORY_REVIEW_LABELS,
  type AdminStoryRowDto,
  type StoryReviewFilter,
  type StorySort,
  type StoryVisibilityFilter,
} from '@api/types/stories'
import { errorMessage, formatDay } from '@features/admin/components/users/userFormatting'
import { formatStars, storyAccessSummary, storyShape } from '../storyFormatting'
import {
  FeaturedRankDialog,
  StoryApproveDialog,
  StoryRejectDialog,
  StoryVisibilityDialog,
} from '../components/StoryDialogs'
import { StoryReviewsTab } from '../components/StoryReviewsTab'
import { STORIES_KEY } from '../storyQueryKeys'

const REVIEW_CHIP_COLORS: Record<number, 'default' | 'warning' | 'success' | 'error'> = {
  0: 'default',
  1: 'warning',
  2: 'success',
  3: 'error',
}

export default function AdminStoriesPage() {
  const [tab, setTab] = useState('queue')
  const [searchParams, setSearchParams] = useSearchParams()

  // The queue badge: one tiny read of the pending count (every list response carries it).
  const { data: pendingPage } = useQuery({
    queryKey: [...STORIES_KEY, 'pending-count'],
    queryFn: () => storiesApi.list({ review: 'pending', page: 1, pageSize: 1 }),
    staleTime: 30_000,
  })
  const pending = pendingPage?.pendingCount ?? 0
  const tabs: ResponsiveTabItem[] = [
    {
      key: 'queue',
      label: 'Review queue',
      count: pendingPage ? pending : undefined,
      icon: <HourglassTop fontSize="small" />,
    },
    { key: 'stories', label: 'Stories', icon: <AutoStories fontSize="small" /> },
    { key: 'reviews', label: 'Reviews', icon: <RateReview fontSize="small" /> },
    { key: 'paid', label: 'Paid', icon: <Paid fontSize="small" /> },
  ]

  // From a row's Reviews action: one URL change both picks the Story and switches the tab
  // (ResponsiveTabs adopts `?tab=` from the URL).
  const openReviews = (story: AdminStoryRowDto) => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'reviews')
    next.set('story', story.id)
    // The name rides along in history state: the Reviews tab has no read to look a Story up by id.
    setSearchParams(next, { replace: true, state: { storyName: story.name } })
  }

  return (
    <PageShell
      title="Stories"
      subtitle="Moderation only: Stories are written and edited on the website."
    >
      <ResponsiveTabs tabs={tabs} value={tab} onChange={setTab} sx={{ mb: 2 }} />
      {tab === 'queue' && <StoriesListTab key="queue" queue onOpenReviews={openReviews} />}
      {tab === 'stories' && <StoriesListTab key="stories" onOpenReviews={openReviews} />}
      {tab === 'reviews' && <StoryReviewsTab />}
      {tab === 'paid' && (
        <Alert severity="info">
          Paid stories are not available yet. When they are, every fee Story will wait here for approval before it
          can be sold.
        </Alert>
      )}
    </PageShell>
  )
}

/**
 * The Story list. `queue`: the review queue, Pending only, oldest submission first. Otherwise every Story with a
 * review filter, Pending first by default.
 */
function StoriesListTab({
  onOpenReviews,
  queue = false,
}: {
  onOpenReviews: (s: AdminStoryRowDto) => void
  queue?: boolean
}) {
  const { user } = useAuthStore()
  const isAdmin = !!user?.roles?.includes('Admin')

  const [search, setSearch] = useState('')
  const [visibility, setVisibility] = useState<StoryVisibilityFilter>('all')
  const [reviewFilter, setReviewFilter] = useState<StoryReviewFilter>('all')
  const review: StoryReviewFilter = queue ? 'pending' : reviewFilter
  const [sort, setSort] = useState<StorySort>('queue')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const [featuredTarget, setFeaturedTarget] = useState<AdminStoryRowDto | null>(null)
  const [visibilityTarget, setVisibilityTarget] = useState<AdminStoryRowDto | null>(null)
  const [approveTarget, setApproveTarget] = useState<AdminStoryRowDto | null>(null)
  const [rejectTarget, setRejectTarget] = useState<AdminStoryRowDto | null>(null)

  const effectiveSort: StorySort = queue ? 'queue' : sort
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [...STORIES_KEY, 'list', { search, visibility, review, sort: effectiveSort, page, pageSize }],
    queryFn: () => storiesApi.list({ search, visibility, review, sort: effectiveSort, page: page + 1, pageSize }),
    staleTime: 30_000,
  })

  const columns: DataTableColumn<AdminStoryRowDto>[] = [
    {
      key: 'name',
      header: 'Story',
      primary: true,
      render: (s) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
            {s.name}
          </Typography>
          {s.tagline && (
            // Wrapped, not ellipsised: the rest used to live only in a hover title a phone never shows.
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: { md: 360 }, overflowWrap: 'anywhere' }}>
              {s.tagline}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'review',
      header: 'Review',
      render: (s) => (
        <Box sx={{ minWidth: 0 }}>
          <Chip
            size="small"
            color={REVIEW_CHIP_COLORS[s.storyReview] ?? 'default'}
            variant={s.storyReview === 0 ? 'outlined' : 'filled'}
            label={STORY_REVIEW_LABELS[s.storyReview] ?? String(s.storyReview)}
          />
          {s.storyReview === 1 && s.storySubmittedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              since {formatDay(s.storySubmittedAt)}
            </Typography>
          )}
          {s.storyReview === 3 && s.storyReviewNote && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: { md: 260 }, overflowWrap: 'anywhere' }}>
              {s.storyReviewNote}
            </Typography>
          )}
        </Box>
      ),
    },
    { key: 'area', header: 'Area', hideBelow: 'md', render: (s) => s.areaLabel || '—' },
    {
      key: 'public',
      header: 'Visibility',
      render: (s) => (
        <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip size="small" color={s.isPublic ? 'success' : 'default'} variant={s.isPublic ? 'filled' : 'outlined'} label={s.isPublic ? 'Public' : 'Hidden'} />
          {!s.isActive && <Chip size="small" color="warning" variant="outlined" label="Inactive" />}
        </Box>
      ),
    },
    { key: 'shape', header: 'Stops / chapters', render: storyShape },
    {
      key: 'rating',
      header: 'Rating',
      align: 'right',
      render: (s) => formatStars(s.ratingAverage, s.ratingCount),
    },
    { key: 'reviews', header: 'Reviews', align: 'right', hideBelow: 'md', render: (s) => s.reviewCount },
    {
      key: 'featured',
      header: 'Featured',
      align: 'right',
      render: (s) => (s.featuredRank != null ? `#${s.featuredRank}` : '—'),
    },
    { key: 'access', header: 'Access', hideBelow: 'md', render: storyAccessSummary },
    { key: 'creator', header: 'Owner', hideBelow: 'lg', render: (s) => s.creatorDisplayName || s.creatorId.slice(0, 8) },
    {
      key: 'dates',
      header: 'Created / published',
      hideBelow: 'lg',
      render: (s) => `${formatDay(s.createdDate)} / ${s.storyPublishedAt ? formatDay(s.storyPublishedAt) : '—'}`,
    },
  ]

  // Labelled buttons + "More" on a phone card, icons with tooltips in the table. The first two
  // (approve, reviews) are what a moderator reaches for; reject is destructive so it sits in More.
  const rowActions = (s: AdminStoryRowDto): RowAction[] => {
    const actions: RowAction[] = []
    if (s.storyReview !== 2)
      actions.push({ key: 'approve', label: 'Approve', icon: <CheckCircleOutline fontSize="small" />, color: 'success', onClick: () => setApproveTarget(s) })
    actions.push({ key: 'reviews', label: 'Reviews', icon: <RateReview fontSize="small" />, onClick: () => onOpenReviews(s) })
    actions.push({
      key: 'open',
      label: 'Open',
      hint: 'Open on the play site',
      icon: <OpenInNew fontSize="small" />,
      onClick: () => window.open(storyPlaySiteUrl(s.id), '_blank', 'noopener,noreferrer'),
    })
    if (isAdmin)
      actions.push({
        key: 'featured',
        label: s.featuredRank != null ? 'Featured rank' : 'Feature',
        hint: s.featuredRank != null ? 'Change or clear the Featured rank' : undefined,
        icon: s.featuredRank != null ? <Star fontSize="small" color="warning" /> : <StarBorder fontSize="small" />,
        onClick: () => setFeaturedTarget(s),
      })
    actions.push({
      key: 'visibility',
      label: s.isPublic ? 'Hide' : 'Unhide',
      hint: s.isPublic ? 'Hide story' : 'Unhide story',
      icon: s.isPublic ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />,
      onClick: () => setVisibilityTarget(s),
    })
    if (s.storyReview !== 3)
      actions.push({ key: 'reject', label: 'Reject', hint: 'Reject with a reason', icon: <HighlightOff fontSize="small" />, color: 'error', onClick: () => setRejectTarget(s) })
    return actions
  }

  // Three lines instead of the generic ten-row label/value stack.
  const renderCard = (s: AdminStoryRowDto) => (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
          {s.name}
        </Typography>
        <Chip
          size="small"
          color={REVIEW_CHIP_COLORS[s.storyReview] ?? 'default'}
          variant={s.storyReview === 0 ? 'outlined' : 'filled'}
          label={STORY_REVIEW_LABELS[s.storyReview] ?? String(s.storyReview)}
        />
      </Box>
      {s.tagline && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
          {s.tagline}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
        {[
          s.isPublic ? 'Public' : 'Hidden',
          !s.isActive ? 'Inactive' : null,
          s.areaLabel || null,
          storyShape(s),
          formatStars(s.ratingAverage, s.ratingCount),
          s.featuredRank != null ? `Featured #${s.featuredRank}` : null,
          s.storyReview === 1 && s.storySubmittedAt ? `waiting since ${formatDay(s.storySubmittedAt)}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
      {s.storyReview === 3 && s.storyReviewNote && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
          Rejected: {s.storyReviewNote}
        </Typography>
      )}
    </Box>
  )

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <SearchField
          value={search}
          onSearch={(value) => {
            setSearch(value.trim())
            setPage(0)
          }}
          placeholder="Search name, area or tagline"
          sx={{ flex: '1 1 220px' }}
        />
        <FormControl size="small" sx={{ flex: '0 1 150px', minWidth: 120 }}>
          <InputLabel>Visibility</InputLabel>
          <Select
            label="Visibility"
            value={visibility}
            onChange={(e) => {
              setVisibility(e.target.value as StoryVisibilityFilter)
              setPage(0)
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="hidden">Hidden</MenuItem>
          </Select>
        </FormControl>
        {!queue && (
          <FormControl size="small" sx={{ flex: '0 1 160px', minWidth: 130 }}>
            <InputLabel>Review</InputLabel>
            <Select
              label="Review"
              value={reviewFilter}
              onChange={(e) => {
                setReviewFilter(e.target.value as StoryReviewFilter)
                setPage(0)
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="none">Not submitted</MenuItem>
            </Select>
          </FormControl>
        )}
        {!queue && (
          <FormControl size="small" sx={{ flex: '0 1 170px', minWidth: 130 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              label="Sort"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as StorySort)
                setPage(0)
              }}
            >
              <MenuItem value="queue">Pending first</MenuItem>
              <MenuItem value="featured">Featured first</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="created">Newest</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>
      {queue && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Any user can make a Story. Nothing here is on the Stories shelf until it is approved. Check the text, cover,
          content notes and access, then approve it or reject it with a reason for its editors.
        </Alert>
      )}

      <DataTable
        rows={data?.items ?? []}
        columns={columns}
        getRowKey={(s) => s.id}
        loading={isLoading}
        error={error ? errorMessage(error, 'Failed to load stories') : null}
        emptyMessage={queue ? 'No stories are waiting for review' : 'No stories'}
        dense
        page={page}
        pageSize={pageSize}
        total={data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(0)
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        pagerPosition="both"
        onRetry={() => void refetch()}
        getRowActions={rowActions}
        renderCard={renderCard}
      />

      {featuredTarget && <FeaturedRankDialog story={featuredTarget} onClose={() => setFeaturedTarget(null)} />}
      {visibilityTarget && <StoryVisibilityDialog story={visibilityTarget} onClose={() => setVisibilityTarget(null)} />}
      {approveTarget && <StoryApproveDialog story={approveTarget} onClose={() => setApproveTarget(null)} />}
      {rejectTarget && <StoryRejectDialog story={rejectTarget} onClose={() => setRejectTarget(null)} />}
    </>
  )
}
